import { LicenseDelete, LicensePost, LicenseProviderList } from "../generated/types";
import { apiFetchJson } from "./apiFetchRoute";

/**
 * Codes licence gérés par la plateforme. Il n'existe pas (encore) d'enum généré côté
 * DATA pour ces codes : cette union est donc la référence côté front, et sert à filtrer
 * ce que renvoient les routes licence (`response: "x"`, donc non typé).
 */
export type LicenseOption =
  | "alimentation_precision"
  | "compteur_elec"
  | "compteur_gaz"
  | "data_extraction"
  | "hygro_ext"
  | "maj_usb"
  | "pesage_silo"
  | "pesage_volaille"
  | "security_codes"
  | "ventilation_jardin";

/** Ordre d'affichage des licences dans le modal, et liste de référence des codes connus. */
export const LICENSE_OPTIONS: readonly LicenseOption[] = [
  "alimentation_precision",
  "compteur_elec",
  "compteur_gaz",
  "data_extraction",
  "hygro_ext",
  "maj_usb",
  "pesage_silo",
  "pesage_volaille",
  "security_codes",
  "ventilation_jardin",
] as const;

const isLicenseOption = (code: unknown): code is LicenseOption =>
  typeof code === "string" &&
  (LICENSE_OPTIONS as readonly string[]).includes(code);

/**
 * Compteurs d'une licence pour une entreprise :
 *  - `total`  : licences détenues par l'entreprise (achetées / attribuées à ses comptes) ;
 *  - `active` : licences déjà activées sur ses produits, celle du produit courant comprise.
 *
 * `total - active` est donc le stock encore disponible pour une nouvelle activation.
 */
export type LicenseOptionSummary = {
  total: number;
  active: number;
};

export type LicenseSummary = Record<LicenseOption, LicenseOptionSummary>;

/** Summary vide : sert de repli tant que le fetch n'a pas répondu, ou en cas d'erreur. */
export const emptyLicenseSummary = (): LicenseSummary =>
  LICENSE_OPTIONS.reduce((acc, code) => {
    acc[code] = { total: 0, active: 0 };
    return acc;
  }, {} as LicenseSummary);

/** Premier champ numérique présent parmi `names`, 0 si aucun n'est exploitable. */
const pickCount = (row: Record<string, unknown>, names: string[]): number => {
  for (const name of names) {
    const value = Number(row[name]);
    if (Number.isFinite(value)) return value;
  }
  return 0;
};

/**
 * Complète la réponse du back pour qu'elle porte TOUJOURS les 10 codes.
 *
 * La route n'énumère que les licences connues en base pour l'entreprise : un code jamais
 * commandé est absent de la réponse, pas à zéro. Or le modal affiche les 10 licences
 * existantes, y compris celles à 0 — sans ce remplissage, `summary[code]` est `undefined`
 * et le rendu casse.
 *
 * Deux formes sont acceptées, la route n'étant pas typée (`response: "x"`) :
 *  - l'objet indexé par code : `{ "pesage_silo": { total: 3, active: 1 } }` ;
 *  - la liste de lignes : `[{ code: "pesage_silo", total: 3, active: 1 }]`.
 */
const normalizeLicenseSummary = (raw: unknown): LicenseSummary => {
  const summary = emptyLicenseSummary();
  if (!raw || typeof raw !== "object") return summary;

  // `[code, counts]` dans les deux cas : les lignes d'un tableau portent leur code à
  // l'intérieur, on le remonte pour traiter les deux formes de la même façon.
  const entries: [unknown, unknown][] = Array.isArray(raw)
    ? raw.map((row) => [
        row && typeof row === "object"
          ? (row.code ?? row.license_code ?? row.name)
          : undefined,
        row,
      ])
    : Object.entries(raw as Record<string, unknown>);

  let matched = 0;
  entries.forEach(([code, counts]) => {
    if (!isLicenseOption(code) || !counts || typeof counts !== "object") return;
    const row = counts as Record<string, unknown>;
    summary[code] = {
      total: pickCount(row, ["total", "nb_total", "qty", "nb"]),
      active: pickCount(row, ["active", "nb_active", "used", "nb_used"]),
    };
    matched += 1;
  });

  // Une réponse non vide dont aucun code n'est reconnu = forme inattendue. Sans ce log, le
  // modal afficherait simplement des zéros partout, ce qui se confond avec « aucune licence ».
  if (matched === 0 && entries.length > 0) {
    console.warn("Summary licence non reconnu, réponse brute :", raw);
  }

  return summary;
};

/**
 * Codes licence actifs sur un produit.
 *
 * Les codes inconnus du front sont écartés : le back peut en introduire un nouveau avant
 * que `LicenseOption` ne soit mis à jour, et on préfère ne pas l'afficher plutôt que de
 * casser le modal (il n'aurait ni libellé ni icône).
 */
export const fetchLicenseUuid = async (
  uuid: string
): Promise<{
  license: LicenseOption[];
  error: string | null;
}> => {
  try {
    const data = (await apiFetchJson("getLicenseUuid", undefined, {
      params: { uuid },
    })) as string[] | undefined;
    return { license: (data ?? []).filter(isLicenseOption), error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { license: [], error: "Impossible de charger les licenses" };
  }
};

/**
 * Compteurs de licences (détenues / activées) d'une entreprise.
 *
 * Renvoie toujours les 10 codes, y compris ceux absents de la réponse (cf.
 * `normalizeLicenseSummary`) : les appelants peuvent indexer `summary[code]` sans garde.
 */
export const fetchLicenseSummaryId_company = async (
  id_company: string
): Promise<{
  license: LicenseSummary | null;
  error: string | null;
}> => {
  try {
    const data = await apiFetchJson("getLicenseSummaryId_company", undefined, {
      params: { id_company },
    });
    return { license: normalizeLicenseSummary(data), error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { license: null, error: "Impossible de charger les licenses" };
  }
};

/**
 * Installateurs partenaires d'une entreprise, avec leur stock de licences.
 *
 * Alimente le choix « chez quel installateur puiser ? » : un admin/PSC peut attribuer une
 * licence à un produit d'éleveur en la prenant sur le stock d'un installateur partenaire
 * de cet éleveur. L'`idCompany` attendu est donc celui de l'entreprise PROPRIÉTAIRE DU
 * PRODUIT, pas celui de l'admin connecté.
 *
 * Le back n'inclut que les partenariats de type `installer` au statut `accepted` — et
 * REVÉRIFIE la même règle à l'attribution : l'écran ne peut donc pas proposer un choix
 * qui serait refusé ensuite.
 *
 * `?? []` : la route répond **204** quand l'entreprise n'a aucun partenaire installateur
 * accepté. C'est un cas normal, que l'appelant doit distinguer d'une erreur.
 */
export const fetchLicenseProviders = async (
  idCompany: number
): Promise<{ providers: LicenseProviderList; error: string | null }> => {
  try {
    const data = (await apiFetchJson(
      "getAdminLicenseProvidersId_company",
      undefined,
      { params: { id_company: idCompany } }
    )) as LicenseProviderList | undefined;
    return { providers: data ?? [], error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { providers: [], error: "Impossible de charger les installateurs partenaires" };
  }
};

/**
 * Active des licences sur un produit.
 *
 * Le back attend `{ uuid, codes }` ; le type généré `LicensePost` annonce encore
 * `licenseOrderCodes` — il est en retard sur la route, d'où le cast. À retirer à la
 * prochaine régénération de `src/generated/types.ts`.
 */
export const activateLicenses = async (
  uuid: string,
  codes: LicenseOption[],
  idCompanyStock?: number
): Promise<{ error: string | null }> => {
  try {
    // `id_company_stock` (optionnel) : réservé aux admin/PSC — désigne l'installateur
    // dont le stock est débité. Omis, le back consomme celui de l'appelant, ce qui
    // préserve le comportement historique pour un installateur.
    await apiFetchJson("postLicense", {
      uuid,
      codes,
      ...(idCompanyStock ? { id_company_stock: idCompanyStock } : {}),
    } as unknown as LicensePost);
    return { error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { error: "Impossible d'activer les licenses" };
  }
};

/**
 * Retire UNE licence d'un produit (réservé aux comptes admin / psc).
 *
 * Comme pour l'activation, `LicenseDelete` ne déclare que `uuid` alors que la route
 * accepte le code à retirer : cast à supprimer après régénération des types.
 */
export const removeLicense = async (
  uuid: string,
  code: LicenseOption
): Promise<{ error: string | null }> => {
  try {
    await apiFetchJson("deleteLicense", {
      uuid,
      code,
    } as unknown as LicenseDelete);
    return { error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { error: "Impossible de retirer la license" };
  }
};
